import React, { useContext, useEffect, useState } from 'react'

import { Container, IntegrationsGridPage, Text, UserContext } from '@doist/marketist'

import { Footer, Navbar, Seo } from 'components'
import groupBy from 'lodash/groupBy'
import { GetServerSidePropsContext } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { getCommonServerSideProps } from 'services'
import { IntegrationsClient as contentful } from 'services/contentful'
import { getOgImageURL } from 'services/ogimage'
import { Category, Integration } from 'types/contentful'

import css from './index.module.css'

type ZapierIntegration = {
    name: string
    outlink: string
    commonContent: {
        appLogo: {
            alt: string
            url: string
        }
    }
}

type IntegrationsProps = {
    categories: Category[]
    featuredSlugs: string[]
    contentfulIntegrations: Integration[]
}

export default function Integrations({
    categories,
    featuredSlugs,
    contentfulIntegrations,
}: IntegrationsProps) {
    const utm_campaign = 'integrations'
    const [zaps, setZaps] = useState<ZapierIntegration[]>([])
    const [showHeader, setShowHeader] = useState(true)
    const { t, i18n } = useTranslation(['integrations', 'common'])
    const { isAuthenticated } = useContext(UserContext)
    const ogimage = getOgImageURL('og-image-integrations.jpg', i18n.language)
    const ogimages = [{ url: ogimage, width: 1200, height: 628, alt: t('seoImageAlt') }]

    useEffect(() => {
        ;(async function fetchZapierIntegrations() {
            /** use lazy load for the zapier integrations file, which is ~120kb */
            setZaps((await import('public/assets/integrations/zapierIntegrations')).default)
        })()
    }, [])

    return (
        <>
            <Seo
                title={t('seoTitle')}
                description={t('seoDescription')}
                path="/integrations"
                lang={i18n.language}
                images={ogimages}
            />

            <Navbar isAuth={isAuthenticated} />

            {showHeader && (
                <Container className={css.header} width="base">
                    <Text tag="h1" size="major-2xl" className={css.title}>
                        {t('title')}
                    </Text>
                    <Text tag="h2" size="body-lg" className={css.subtitle}>
                        {t('subtitle')}
                    </Text>
                </Container>
            )}

            <IntegrationsGridPage
                onFilter={(category, searchString) => {
                    setShowHeader(category === 'all' && searchString === '')
                }}
                language={i18n.language}
                categories={categories}
                integrations={contentfulIntegrations}
                zapierIntegrations={zaps}
                featuredSlugs={featuredSlugs}
                ctaSubtitle={t('ctaSubtitle')}
                ctaLink="https://developer.twist.com/v3/"
                searchLabel={t('search', { ns: 'common' })}
                clearLabel={t('clearSearch', { ns: 'common' })}
                allCategoryLabel={t('integrations.categoriesAll', { ns: 'common' })}
                allIntegrationsLabel={t('integrations.allIntegrations', { ns: 'common' })}
                categoriesTitle={t('integrations.categoriesTitle', { ns: 'common' })}
                ctaButtonTitle={t('integrations.ctaButton', { ns: 'common' })}
                ctaTitle={t('integrations.ctaTitle', { ns: 'common' })}
                ctaTitleNoResult={t('integrations.ctaNoResultsTitle', { ns: 'common' })}
                featuredIntegrationLabel={t('integrations.featuredIntegration', { ns: 'common' })}
                loadMoreLabel={t('integrations.loadMore', { ns: 'common' })}
                noResultSubtitle={t('integrations.noResultsSub', { ns: 'common' })}
                noResultsLabel={(keyword) => t('integrations.noResults', { keyword, ns: 'common' })}
                resultForLabel={(count, keyword) =>
                    t('integrations.resultsFor', { count, keyword, ns: 'common' })
                }
                searchTitle={t('integrations.searchBar', { ns: 'common' })}
                viaLabel={t('integrations.via', { ns: 'common' })}
            />

            <Footer utm_campaign={utm_campaign} />
        </>
    )
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
    const { locale, ...commonProps } = await getCommonServerSideProps(ctx)

    const contentfulIntegrations = await contentful.getIntegrations(locale)
    const allCategories = await contentful.getCategories(locale)
    const featuredSlugs = await contentful.getFeaturedSlugs()
    const anyCategory = Object.keys(groupBy(contentfulIntegrations, 'commonContent.category.slug'))
    const categories = allCategories.filter((c: Category) => anyCategory.includes(c.slug))

    return {
        props: {
            categories,
            featuredSlugs,
            contentfulIntegrations,
            ...commonProps,
            ...(await serverSideTranslations(locale, ['common', 'integrations'])),
        },
    }
}
